import "./OptionsModal.scss";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import getIcon from "../../utility/getIcon";
import GradientButton from "../buttons/gradientButton/GradientButton";
import { useSelector } from "react-redux";
import { useAlert } from "react-alert";
import { useTranslation } from "react-i18next";

function OptionsModal({ visible, categories = [], onSelectOptions, onClose, orgId }) {
  const [index, setIndex] = useState(0);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const cart = useSelector((state) => state.cartReducer.cartByOrg[orgId] || []);
  const alert = useAlert();
  const { t } = useTranslation("fastpass");

  useEffect(() => {
    if (visible) {
      setIndex(0);
      setSelectedOptions([]);

      const filteredCategories = categories
        ?.map((category) => {
          const filteredProducts = category.products?.filter((product) => {
            if (product.outOfStock) return false;
            if (product.availableQuantity === false) return true;

            const matchingProducts = cart?.filter(
              (cartProduct) => cartProduct.id.split(":")[0] === product.id,
            );

            const quantityInCart = matchingProducts.reduce((sum, p) => sum + (p.quantity || 0), 0);
            return product.availableQuantity - quantityInCart > 0;
          });

          return filteredProducts.length > 0 ? { ...category, products: filteredProducts } : null;
        })
        .filter(Boolean);

      if (!filteredCategories?.length) {
        alert.show(t("noQuantityAvailable"), { type: "warning" });
        onClose();
        return;
      }

      setFilteredCategories(filteredCategories);
    }
  }, [visible, categories, cart, alert]);

  const current = filteredCategories[index];

  const handleSelect = (newOption) => {
    if (filteredCategories[index].multiSelect) {
      setSelectedOptions((prev) => {
        const optionIndex = prev.findIndex((option) => option.id === newOption.id);

        if (optionIndex === -1) return [...prev, newOption];
        return prev.filter((option) => option.id !== prev[optionIndex].id);
      });
    } else {
      if (index + 1 < filteredCategories.length) {
        setSelectedOptions((prev) => [...prev, newOption]);
        setIndex(index + 1);
      } else {
        onClose && onClose();
        onSelectOptions([...selectedOptions, newOption]);
      }
    }
  };

  const validateMultiSelection = () => {
    if (index + 1 < filteredCategories.length) {
      setIndex(index + 1);
    } else {
      onClose && onClose();
      onSelectOptions(selectedOptions);
    }
  };

  const skipOption = () => {
    const filteredOptions = selectedOptions.filter(
      (option) => !current.products.some((product) => product.id === option.id),
    );

    if (index + 1 < filteredCategories.length) {
      setSelectedOptions(filteredOptions);
      setIndex(index + 1);
    } else {
      onClose();
      onSelectOptions(filteredOptions);
    }
  };

  const handleGoBack = () => {
    const currentIndex = index - 1;
    const currentProducts = filteredCategories[currentIndex]?.products;
    setIndex(currentIndex);
    setSelectedOptions((prev) =>
      prev.filter((option) => !currentProducts.some((product) => option.id === product.id)),
    );
  };

  const OptionItem = ({ productDetails, onClick, isSelected }) => {
    return (
      <button className={`option-item ${isSelected ? "active" : ""}`} onClick={onClick}>
        <div className="option-item-image">
          <img
            src={getIcon(productDetails.category, productDetails.customIcon)}
            alt={productDetails.title}
          />
        </div>
        <div className="option-item-details">
          <h3>{productDetails.title}</h3>
          <p className="price">{productDetails.price}€</p>
        </div>
      </button>
    );
  };

  return (
    <Modal show={visible} centered>
      <Modal.Body className="options-modal">
        <div className="options-modal-header">
          {index > 0 && <button onClick={handleGoBack}>{t("back")}</button>}
          <h2 className="options-modal-header-title">{t("options")}</h2>
        </div>
        <div className="options-modal-content">
          <div className="options-modal-content-options">
            {filteredCategories[index]?.products?.map((optionProduct) => (
              <OptionItem
                key={optionProduct.id}
                productDetails={optionProduct}
                onClick={() => handleSelect(optionProduct)}
                isSelected={selectedOptions.some((option) => optionProduct.id === option.id)}
              />
            ))}
          </div>
        </div>

        <div className="options-modal-buttons">
          <GradientButton onPress={skipOption}>{t("skip")}</GradientButton>

          {filteredCategories[index]?.multiSelect && (
            <GradientButton onPress={validateMultiSelection}>{t("validate")}</GradientButton>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default OptionsModal;
